import type { PointCloudObject } from "@/object-cache";
import type { LodPointCloud } from "@faro-lotv/lotv";
import type { CadDistanceReference } from "./cad-distance-reference";

const references = new WeakMap<
  LodPointCloud,
  Map<string, CadDistanceReference>
>();
const listeners = new Set<() => void>();

/**
 * @param listener Consumer notified when an owned reference becomes available or is released.
 * @returns Subscription cleanup for reference replacement and teardown notifications.
 */
export function subscribeCadAnalysisReferences(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * @param cloud Source view.
 * @param id Analysis ID.
 * @returns The ready reference owned by its renderer, if any.
 */
export function getCadAnalysisReference(
  cloud: LodPointCloud,
  id: string,
): CadDistanceReference | undefined {
  return references.get(cloud)?.get(id);
}

/**
 * @param cloud Source view.
 * @param id Analysis ID.
 * @param reference Owned reference.
 * @returns Unregister without disposing another view's reference.
 */
export function registerCadAnalysisReference(
  cloud: PointCloudObject,
  id: string,
  reference: CadDistanceReference,
): () => void {
  let entries = references.get(cloud);
  if (!entries) {
    entries = new Map();
    references.set(cloud, entries);
  }
  entries.set(id, reference);
  listeners.forEach((listener) => listener());
  return () => {
    if (entries.get(id) === reference) {
      entries.delete(id);
      listeners.forEach((listener) => listener());
    }
  };
}
